import { Repository } from "./adapters/repository";
import { ProjectHistory } from "./adapters/projectHistory";

import * as retrieveProject from "./useCases/retrieveProject";
import * as retrieveProjects from "./useCases/retrieveProjects";
import * as createProject from "./useCases/createProject";
import * as editProject from "./useCases/editProject";
import * as removeProject from "./useCases/removeProject";
import * as editProjectWeatherReport from "./useCases/editProjectWeatherReport";
import * as retrieveProjectSynthesis from "./useCases/retrieveProjectSynthesis";
import * as retrieveProjectHistory from "./useCases/retrieveProjectHistory";
import * as pushProjectHistory from "./useCases/pushProjectHistory";
import * as configureProjectTools from "./useCases/configureProjectTools";
import * as retrieveProjectToolsConfiguration from "./useCases/retrieveProjectToolsConfiguration";
import * as retrieveProjectModels from "./useCases/retrieveProjectModels";
import * as useProjectModel from "./useCases/useProjectModel";
import * as createProjectModelFromProject from "./useCases/createProjectModelFromProject";
import * as duplicateProject from "./useCases/duplicateProject";

enum UseCases {
  retrieveProject = "retrieveProject",
  retrieveProjects = "retrieveProjects",
  createProject = "createProject",
  editProject = "editProject",
  removeProject = "removeProject",
  editProjectWeatherReport = "editProjectWeatherReport",
  retrieveProjectSynthesis = "retrieveProjectSynthesis",
  retrieveProjectHistory = "retrieveProjectHistory",
  pushProjectHistory = "pushProjectHistory",
  configureProjectTools = "configureProjectTools",
  retrieveProjectToolsConfiguration = "retrieveProjectToolsConfiguration",
  retrieveProjectModels = "retrieveProjectModels",
  useProjectModel = "useProjectModel",
  createProjectModelFromProject = "createProjectModelFromProject",
  duplicateProject = "duplicateProject",
}

interface Core {
  [UseCases.configureProjectTools]: configureProjectTools.Interactor;
  [UseCases.retrieveProjectToolsConfiguration]: retrieveProjectToolsConfiguration.Interactor;
  [UseCases.retrieveProject]: retrieveProject.Interactor;
  [UseCases.retrieveProjects]: retrieveProjects.Interactor;
  [UseCases.createProject]: createProject.Interactor;
  [UseCases.editProject]: editProject.Interactor;
  [UseCases.removeProject]: removeProject.Interactor;
  [UseCases.editProjectWeatherReport]: editProjectWeatherReport.Interactor;
  [UseCases.retrieveProjectSynthesis]: retrieveProjectSynthesis.Interactor;
  [UseCases.retrieveProjectHistory]: retrieveProjectHistory.Interactor;
  [UseCases.pushProjectHistory]: pushProjectHistory.Interactor;
  [UseCases.retrieveProjectModels]: retrieveProjectModels.Interactor;
  [UseCases.useProjectModel]: useProjectModel.Interactor;
  [UseCases.createProjectModelFromProject]: createProjectModelFromProject.Interactor;
  [UseCases.duplicateProject]: duplicateProject.Interactor;
}

type Boundaries = BoundaryMap<Core>;

type Dependencies = {
  repository: Repository;
  projectHistory: ProjectHistory;
};

function createCore(dependencies: Dependencies): Core {
  return {
    [UseCases.retrieveProject]: retrieveProject.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveProjects]: retrieveProjects.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createProject]: createProject.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editProject]: editProject.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeProject]: removeProject.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editProjectWeatherReport]:
      editProjectWeatherReport.createInteractor(dependencies.repository),
    [UseCases.retrieveProjectSynthesis]:
      retrieveProjectSynthesis.createInteractor(dependencies.repository),
    [UseCases.retrieveProjectHistory]: retrieveProjectHistory.createInteractor(
      dependencies.projectHistory,
      dependencies.repository,
    ),
    [UseCases.pushProjectHistory]: pushProjectHistory.createInteractor(
      dependencies.projectHistory,
    ),
    [UseCases.configureProjectTools]: configureProjectTools.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveProjectToolsConfiguration]:
      retrieveProjectToolsConfiguration.createInteractor(
        dependencies.repository,
      ),
    [UseCases.retrieveProjectModels]: retrieveProjectModels.createInteractor(
      dependencies.repository,
    ),
    [UseCases.useProjectModel]: useProjectModel.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createProjectModelFromProject]:
      createProjectModelFromProject.createInteractor(dependencies.repository),
    [UseCases.duplicateProject]: duplicateProject.createInteractor(
      dependencies.repository,
    ),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
